import React, { useState, useEffect } from 'react';
import { useUser, useAuth } from '@clerk/clerk-react';

export default function ContractorBusinessTab() {
  const { user } = useUser();
  const { getToken } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [form, setForm] = useState({
    company: '',
    businessAddress: '',
    phone: '',
  });

  useEffect(() => {
    const load = async () => {
      if (!user?.id) { setLoading(false); return; }
      try {
        const token = await getToken();
        if (!token) throw new Error('Authentication required');
        const { getApiUrl } = await import('../../lib/getApiUrl');
        const r = await fetch(getApiUrl('/api/contractors/profile'), {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!r.ok) throw new Error('Failed to load profile');
        const data = await r.json();
        setForm({
          company: data.company ?? '',
          businessAddress: data.businessAddress ?? '',
          phone: data.phone ?? '',
        });
      } catch {
        // leave form empty; user can fill in
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [user?.id, getToken]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSave = async () => {
    if (!user?.id) return;
    setSaving(true);
    setError(null);
    try {
      const token = await getToken();
      if (!token) throw new Error('Authentication required');
      const { getApiUrl } = await import('../../lib/getApiUrl');
      const r = await fetch(getApiUrl('/api/contractors/profile'), {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          company: form.company || undefined,
          businessAddress: form.businessAddress || undefined,
          phone: form.phone || undefined,
        }),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(String(d.error || 'Failed to save'));
      }
      setSuccess(true);
      setTimeout(() => setSuccess(false), 2000);
    } catch (err: any) {
      setError(err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const stopModalBubble = (e: React.MouseEvent) => e.stopPropagation();

  if (loading) return <div style={{ textAlign: 'center', padding: '20px' }}>Loading...</div>;

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '8px 12px',
    border: '1px solid #cbd5e1',
    borderRadius: '6px',
    fontSize: '14px',
    color: '#1e293b',
    background: 'white',
    boxSizing: 'border-box',
  };
  const labelStyle: React.CSSProperties = {
    display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px', color: '#334155',
  };

  return (
    <div onClick={stopModalBubble} className="corebids-settings" style={{ padding: '24px', maxWidth: '600px' }}>
      <div style={{ background: 'white', borderRadius: '8px', border: '1px solid #e2e8f0', padding: '24px' }}>
        <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '24px', color: '#0f172a' }}>
          Business Information
        </h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div>
            <label htmlFor="company" style={labelStyle}>Company Name</label>
            <input id="company" name="company" value={form.company} onChange={handleChange}
              placeholder="Enter your company name" style={inputStyle} />
          </div>
          <div>
            <label htmlFor="businessAddress" style={labelStyle}>Business Address</label>
            <input id="businessAddress" name="businessAddress" value={form.businessAddress} onChange={handleChange}
              placeholder="Enter your business address" style={inputStyle} />
          </div>
        </div>

        <h3 style={{ fontSize: '18px', fontWeight: '600', margin: '28px 0 16px', color: '#0f172a' }}>
          Business Contact
        </h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div>
            <label htmlFor="phone" style={labelStyle}>Business Phone</label>
            <input id="phone" name="phone" type="tel" value={form.phone} onChange={handleChange}
              placeholder="Enter your business phone number" style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Email</label>
            <input value={user?.primaryEmailAddress?.emailAddress ?? ''} disabled
              style={{ ...inputStyle, background: '#f8fafc', color: '#64748b', cursor: 'not-allowed' }} />
            <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#94a3b8' }}>
              Email is managed through your account settings
            </p>
          </div>
        </div>

        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          style={{
            marginTop: '24px',
            padding: '12px',
            width: '100%',
            backgroundColor: saving ? '#ccc' : '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: saving ? 'not-allowed' : 'pointer',
            fontWeight: '500',
            fontSize: '14px',
          }}
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </button>

        {success && (
          <div style={{ marginTop: '12px', padding: '12px 16px', background: '#f0fdf4', color: '#16a34a',
            borderRadius: '8px', fontSize: '14px', border: '1px solid #86efac', textAlign: 'center' }}>
            ✅ Changes saved successfully!
          </div>
        )}
        {error && (
          <div style={{ marginTop: '12px', padding: '12px 16px', background: '#fef2f2', color: '#dc2626',
            borderRadius: '8px', fontSize: '14px', border: '1px solid #fecaca' }}>
            {error}
          </div>
        )}
      </div>
    </div>
  );
}
